import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Avatar, Box, Card, Chip, CircularProgress, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import {
  Bookmark as SavedIcon,
  BookmarkBorder as SaveIcon,
  CheckCircle as AppliedIcon,
  InfoOutlined as DetailsIcon,
  LocationOnOutlined as LocationIcon,
  TaskAltOutlined as ReadyIcon,
} from '@mui/icons-material';
import { Job, statusMeta } from '../../types';
import { formatDateTime } from '../../utils/format';
import { PrefillOnOpen, prefillLinkProps } from '../../hooks/usePrefillOnOpen';
import {
  AGENCY_HINT,
  AGENCY_LABEL,
  atsLabel,
  coverageLabel,
  externalUrl,
  isAppliedStatus,
  jobTypeLabel,
  levelLabel,
  postedAgo,
  workplaceLabel,
} from '../../utils/jobBoard';

interface Props {
  job: Job;
  /** True while this job's detail is open over the list. */
  selected: boolean;
  saving: boolean;
  /** "Prefill on open" for this viewer; 'off' when signed out. */
  prefill: PrefillOnOpen;
  /** Show the job's details (questions, description) over the list. */
  onOpen: (job: Job) => void;
  onToggleSave: (job: Job) => void;
}

const MAX_TECH = 4;

const tagSx = { height: 22, fontSize: '0.75rem' } as const;

// Sits above the card's stretched link so it stays clickable.
const statusChipSx = {
  ...tagSx,
  position: 'relative',
  zIndex: 1,
  fontWeight: 600,
  color: 'white',
  '& .MuiChip-icon': { color: 'white' },
} as const;

/** A plain left click; anything else (new tab, new window) is left to the browser. */
const isPlainClick = (e: React.MouseEvent): boolean =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

const JobCard: React.FC<Props> = ({ job, selected, saving, prefill, onOpen, onToggleSave }) => {
  const status = job.application_status;
  const meta = status ? statusMeta(status) : null;
  const applied = isAppliedStatus(status);
  // Only a plain saved job can be un-saved here; later stages are managed on
  // the application itself.
  const canToggleSave = !status || status === 'saved';
  const tech = job.tech_stack ?? [];
  // The card opens the company's own page: the posting, or the application
  // form itself when the form is to be prefilled as it opens.
  const postingUrl = externalUrl(job.source_url) || externalUrl(job.application_url);
  const openUrl = prefill !== 'off' ? externalUrl(job.application_url) || postingUrl : postingUrl;
  // Full time is the norm and says nothing; an internship already shows as its level.
  const showJobType =
    Boolean(job.job_type) &&
    job.job_type !== 'full_time' &&
    jobTypeLabel(job.job_type) !== levelLabel(job.experience_level);

  return (
    <Card
      component="article"
      variant="outlined"
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 1.5,
        gap: 0.75,
        boxShadow: 'none',
        borderColor: selected ? 'primary.main' : 'divider',
        borderLeft: meta ? `4px solid ${meta.color}` : undefined,
        transition: 'box-shadow 0.15s, border-color 0.15s',
        '&:hover': { boxShadow: 3 },
        '&:focus-within': { borderColor: 'primary.main' },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar
          src={job.company.logo_url || undefined}
          alt=""
          variant="rounded"
          sx={{ width: 28, height: 28, fontSize: 13, bgcolor: 'grey.200', color: 'text.secondary' }}
        >
          {job.company.name?.[0]?.toUpperCase() ?? '?'}
        </Avatar>
        {/* The chip drops under a name it does not fit beside. */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 0.75, rowGap: 0.25 }}>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, maxWidth: '100%', fontWeight: 500 }}>
            {job.company.name}
          </Typography>
          {job.company.is_staffing_agency && (
            <Tooltip title={AGENCY_HINT} describeChild>
              <Chip size="small" variant="outlined" label={AGENCY_LABEL} sx={{ ...tagSx, position: 'relative', zIndex: 1 }} />
            </Tooltip>
          )}
        </Box>
        {meta &&
          (job.application_id ? (
            <Chip
              component={RouterLink}
              to={`/applications/${job.application_id}`}
              clickable
              size="small"
              icon={applied ? <AppliedIcon /> : undefined}
              label={meta.label}
              aria-label={`Your application: ${meta.label}`}
              sx={{ ...statusChipSx, bgcolor: meta.color, '&:hover': { bgcolor: meta.color, opacity: 0.9 } }}
            />
          ) : (
            <Chip
              size="small"
              icon={applied ? <AppliedIcon /> : undefined}
              label={meta.label}
              sx={{ ...statusChipSx, bgcolor: meta.color }}
            />
          ))}
        {openUrl && (
          <Tooltip title="Details and application questions">
            <IconButton
              size="small"
              aria-label={`Details for ${job.title}`}
              onClick={() => onOpen(job)}
              sx={{ m: -0.5, position: 'relative', zIndex: 1 }}
            >
              <DetailsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canToggleSave && (
          <Tooltip title={status === 'saved' ? 'Remove from saved jobs' : 'Save job'}>
            {/* span: a disabled button cannot anchor a tooltip */}
            <Box component="span" sx={{ position: 'relative', zIndex: 1, display: 'inline-flex' }}>
              <IconButton
                size="small"
                color={status === 'saved' ? 'primary' : 'default'}
                aria-label={status === 'saved' ? `Remove ${job.title} from saved jobs` : `Save ${job.title}`}
                aria-pressed={status === 'saved'}
                disabled={saving}
                onClick={() => onToggleSave(job)}
                sx={{ m: -0.5 }}
              >
                {saving ? (
                  <CircularProgress size={18} />
                ) : status === 'saved' ? (
                  <SavedIcon fontSize="small" />
                ) : (
                  <SaveIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Stack>

      {/* A real link, straight to the company's posting in a new tab. Its
          ::after stretches over the card so the whole card is the target.
          A job with no usable link opens its details here instead. */}
      <Link
        {...(openUrl
          ? {
              href: openUrl,
              target: '_blank',
              rel: 'noopener noreferrer',
              ...prefillLinkProps(prefill, openUrl, { jobId: job.id, title: job.title, company: job.company.name }),
            }
          : {
              component: RouterLink,
              to: `/jobs/${job.id}`,
              onClick: (e: React.MouseEvent) => {
                if (!isPlainClick(e)) return;
                e.preventDefault();
                onOpen(job);
              },
            })}
        underline="none"
        color="text.primary"
        aria-label={openUrl ? `${job.title} (opens the company's posting in a new tab)` : undefined}
        sx={{
          fontWeight: 600,
          fontSize: '1rem',
          lineHeight: 1.3,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          overflowWrap: 'anywhere',
          '&::after': { content: '""', position: 'absolute', inset: 0 },
          '&:hover': { color: 'primary.main' },
          '&:focus-visible': { outline: 'none', color: 'primary.main', textDecoration: 'underline' },
        }}
      >
        {job.title}
      </Link>

      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        <LocationIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
        <Typography variant="body2" color="text.secondary" noWrap title={job.location || undefined}>
          {job.location || 'Location not listed'}
        </Typography>
      </Stack>

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {job.workplace_type && (
          <Chip
            size="small"
            variant="outlined"
            color={job.workplace_type === 'remote' ? 'success' : 'default'}
            label={workplaceLabel(job.workplace_type)}
            sx={tagSx}
          />
        )}
        {job.experience_level && (
          <Chip size="small" variant="outlined" label={levelLabel(job.experience_level)} sx={tagSx} />
        )}
        {showJobType && (
          <Chip size="small" variant="outlined" label={jobTypeLabel(job.job_type)} sx={tagSx} />
        )}
        {job.match && (
          <Tooltip
            title={`${job.match.have} of the ${job.match.counted} ${
              job.match.read === 'posting' ? 'skills and tools this posting names' : 'technologies this posting lists'
            } are in your resume and Experience library. Open the job to see which.`}
          >
            <Chip
              size="small"
              variant="outlined"
              color={job.match.score >= 70 ? 'success' : 'default'}
              label={`${job.match.score}% match`}
              sx={{ ...tagSx, position: 'relative', zIndex: 1, fontWeight: job.match.score >= 70 ? 600 : undefined }}
            />
          </Tooltip>
        )}
        {job.coverage && (
          <Chip
            size="small"
            variant={job.coverage.ready ? 'filled' : 'outlined'}
            color={job.coverage.ready ? 'success' : 'default'}
            icon={job.coverage.ready ? <ReadyIcon /> : undefined}
            label={coverageLabel(job.coverage)}
            aria-label={
              job.coverage.ready
                ? 'Ready to apply: every required question is answered'
                : `${coverageLabel(job.coverage)} of the required application questions`
            }
            sx={{ ...tagSx, fontWeight: job.coverage.ready ? 600 : undefined }}
          />
        )}
      </Stack>

      {job.salary_display && (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.dark' }}>
          {job.salary_display}
          <Typography component="span" variant="caption" color="text.secondary">
            {' '}
            a year
          </Typography>
        </Typography>
      )}

      {tech.length > 0 && (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {tech.slice(0, MAX_TECH).map((name) => (
            <Chip key={name} size="small" label={name} sx={{ ...tagSx, bgcolor: 'grey.100' }} />
          ))}
          {tech.length > MAX_TECH && (
            <Chip
              size="small"
              label={`+${tech.length - MAX_TECH}`}
              aria-label={`and ${tech.length - MAX_TECH} more technologies`}
              sx={{ ...tagSx, bgcolor: 'grey.200' }}
            />
          )}
        </Stack>
      )}

      <Box sx={{ flex: 1 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="caption" color="text.secondary" title={formatDateTime(job.posted_at)}>
          {postedAgo(job.posted_at) || 'Date not listed'}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {atsLabel(job.source_platform)}
        </Typography>
      </Stack>
    </Card>
  );
};

export default React.memo(JobCard);
